// pages/tests/MonthlyTestDetail.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Chip,
  Divider,
  Card,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
  Snackbar
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Publish as PublishIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Description as DocIcon,
  InsertDriveFile as FileIcon,
  CalendarToday as CalendarIcon,
  School as SchoolIcon,
  Subject as SubjectIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { filesApi, marksApi, testsApi } from './api/apiClientMonthly';

const MonthlyTestDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [test, setTest] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marks, setMarks] = useState([]);
  const [files, setFiles] = useState([]);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [selectedMark, setSelectedMark] = useState([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchTestDetails();
  }, [id]);

  const fetchTestDetails = async () => {
    setLoading(true);
    try {
      const [testRes, filesRes] = await Promise.all([
        testsApi.getById(id),
        filesApi.getFiles(id),
      ]);

      const testData = testRes?.data ?? testRes;
      const markData = testData?.marks ?? testData?.marks;
      const statisticsData = testData?.statistics ?? testData?.statistics;
      const filesData = filesRes?.data ?? filesRes;

      setTimeout(() => {
        setTest(testData);
        setStatistics(statisticsData)
        setMarks(markData);
        setFiles(filesData);
        setLoading(false);
      }, 500);
    } catch (error) {
      console.error('Error fetching test details:', error);
      setLoading(false);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      DRAFT: { color: 'default', icon: <ScheduleIcon />, label: 'Draft' },
      OPEN: { color: 'warning', icon: <AssessmentIcon />, label: 'Open' },
      COMPLETED: { color: 'info', icon: <CheckCircleIcon />, label: 'Completed' },
      PUBLISHED: { color: 'success', icon: <PublishIcon />, label: 'Published' }
    };

    const config = statusConfig[status] || statusConfig.DRAFT;

    return (
      <Chip
        label={config.label}
        color={config.color}
        icon={config.icon}
      />
    );
  };

  const getGradeColor = (grade) => {
    const colors = {
      'A+': 'success',
      'A': 'success',
      'B+': 'info',
      'B': 'info',
      'C': 'warning',
      'D': 'warning',
      'F': 'error'
    };
    return colors[grade] || 'default';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const handleFileUpload = (event) => {
    const filess = Array.from(event.target.files);
    setFiles(filess);
  };

  const handleRemoveUploadFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return <PdfIcon color="error" />;
    if (fileType?.includes('image')) return <ImageIcon color="primary" />;
    if (fileType?.includes('word') || fileType?.includes('document')) return <DocIcon color="info" />;
    return <FileIcon color="action" />;
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setSnackbar({ open: true, message: 'Please select at least one file', severity: 'warning' });
      return;
    }
    if (!selectedMark) {
      setSnackbar({ open: true, message: 'Please select a student', severity: 'warning' });
      return;
    }

    const file = files?.[0];
    if (!file || !selectedMark) {
      setSnackbar({ open: true, message: 'Please select a file and a student', severity: 'warning' });
      return;
    }

    // Build safe filename
    const ext = file.name.includes('.') ? file.name.substring(file.name.lastIndexOf('.')) : '';
    const safe = String(selectedMark.studentName || '').replace(/[^\w\-]+/g, '_').replace(/_+/g, '_').trim();
    const safeName = `${selectedMark.studentId}_${safe}${ext}`;

    const formData = new FormData();
    formData.append('file', file, safeName);
    formData.append('studentId', selectedMark.studentId);

    try {
      setLoading(true);
      await filesApi.uploadFile(id, formData);
      setSnackbar({ open: true, message: 'File uploaded successfully', severity: 'success' });
      setFiles([]);
      fetchTestDetails();
    } catch (err) {
      setSnackbar({ open: true, message: 'Upload failed', severity: 'error' });
    } finally {
      setLoading(false);
      setUploadDialogOpen(false);
    }

  }
  const handlePublish = async () => {
    try {
      testsApi.publish(id)
      setTest({ ...test, status: 'PUBLISHED' });
      setPublishDialogOpen(false);
    } catch (error) {
      console.error('Error publishing test:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!test) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Test not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/tests')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1">
              {test.testName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              {getStatusChip(test.status)}
              <Chip label={test.academicYear} size="small" variant="outlined" />
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
          >
            Print
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/tests/${id}/edit`)}
          >
            Edit
          </Button>
          {test.status !== 'PUBLISHED' && (
            <Button
              variant="contained"
              color="success"
              startIcon={<PublishIcon />}
              onClick={() => setPublishDialogOpen(true)}
            >
              Publish
            </Button>
          )}
        </Box>
      </Box>

      {/* Info Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <SchoolIcon color="primary" fontSize="small" />
                <Typography color="textSecondary" variant="body2">
                  Class & Section
                </Typography>
              </Box>
              <Typography variant="h6">
                {test.standard} - {test.section}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <SubjectIcon color="primary" fontSize="small" />
                <Typography color="textSecondary" variant="body2">
                  Subject
                </Typography>
              </Box>
              <Typography variant="h6">{test.subject}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CalendarIcon color="primary" fontSize="small" />
                <Typography color="textSecondary" variant="body2">
                  Test Date
                </Typography>
              </Box>
              <Typography variant="h6">
                {format(new Date(test.testDate), 'dd MMM yyyy')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AssessmentIcon color="primary" fontSize="small" />
                <Typography color="textSecondary" variant="body2">
                  Marks
                </Typography>
              </Box>
              <Typography variant="h6">
                {test.passingMarks}/{test.maxMarks}
                <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                  (Pass/Total)
                </Typography>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Statistics */}
      {test.status === 'PUBLISHED' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Test Statistics
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={3}>
              <Typography color="textSecondary" variant="body2">Average Marks</Typography>
              <Typography variant="h5">{statistics.averageMarks}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography color="textSecondary" variant="body2">Highest Marks</Typography>
              <Typography variant="h5" color="success.main">{statistics.highestMarks}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography color="textSecondary" variant="body2">Lowest Marks</Typography>
              <Typography variant="h5" color="error.main">{statistics.lowestMarks}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography color="textSecondary" variant="body2">Pass Percentage</Typography>
              <Typography variant="h5">{statistics.passPercentage}%</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Instructions */}
      {test.instructions && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Instructions
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {test.instructions}
          </Typography>
        </Paper>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={`Marks (${marks.length})`} icon={<AssessmentIcon />} iconPosition="start" />
          <Tab label={`Files (${files.length})`} icon={<PdfIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Roll No</TableCell>
                <TableCell>Student Name</TableCell>
                <TableCell align="center">Marks Obtained</TableCell>
                <TableCell align="center">Grade</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell>Remarks</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {marks.map((mark) => (
                <TableRow key={mark.id} hover>
                  <TableCell>{mark.rollNumber}</TableCell>
                  <TableCell>{mark.studentName}</TableCell>
                  <TableCell align="center">
                    {mark.absent ? '-' : `${mark.marksObtained}/${test.maxMarks}`}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={mark.grade}
                      color={getGradeColor(mark.grade)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {mark.absent ? (
                      <Chip label="Absent" color="error" size="small" />
                    ) : mark.marksObtained >= test.passingMarks ? (
                      <Chip label="Pass" color="success" size="small" />
                    ) : (
                      <Chip label="Fail" color="error" size="small" />
                    )}
                  </TableCell>
                  <TableCell>{mark.remarks}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => { setUploadDialogOpen(true); setSelectedMark(mark); }}
                    >
                      <CloudUploadIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {activeTab === 1 && (
        <Paper>
          <List>
            {files.map((file) => (
              <ListItem key={file.id} divider>
                <ListItemIcon>
                  <PdfIcon color="error" />
                </ListItemIcon>
                <ListItemText
                  primary={file.fileName}
                  secondary={
                    <>
                      {formatFileSize(file.fileSize)} •
                      Uploaded {format(new Date(file.uploadedAt), 'dd MMM yyyy')}
                      {file.studentId && ' • Student specific'}
                    </>
                  }
                />
                <Tooltip title="Download">
                  <IconButton edge="end">
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              </ListItem>
            ))}
            {files.length === 0 && (
              <ListItem>
                <ListItemText primary="No files uploaded" />
              </ListItem>
            )}
          </List>
        </Paper>
      )}

      {/* file upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)}>
        <DialogTitle>Upload files or Attachments</DialogTitle>
        <DialogContent>
          <Grid item xs={12}>
            <Paper
              sx={{
                // p: 3,
                border: '2px dashed',
                borderColor: 'divider',
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover'
                }
              }}
              component="label"
            >
              <input
                type="file"
                hidden
                onChange={handleFileUpload}
                accept=".pdf"
              />
              <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
              <Typography variant="body1">
                Click to upload files or drag and drop
              </Typography>
              <Typography variant="caption" color="textSecondary">
                PDF (max 10MB each)
              </Typography>

              {files.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected Files ({files.length})
                  </Typography>
                  <List dense>
                    {files.map((file, index) => (
                      <ListItem
                        key={index}
                        divider
                        secondaryAction={
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleRemoveUploadFile(index)}
                            disabled={loading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemIcon>
                          {getFileIcon(file.type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={file.name}
                          secondary={formatFileSize(file.size)}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Paper>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpload} variant="contained" color="success">
            upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish Dialog */}
      <Dialog open={publishDialogOpen} onClose={() => setPublishDialogOpen(false)}>
        <DialogTitle>Publish Test Results</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to publish the results for "{test.testName}"?
            Once published, students and parents will be able to view the results.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialogOpen(false)}>Cancel</Button>
          <Button onClick={handlePublish} variant="contained" color="success">
            Publish Results
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default MonthlyTestDetails;